import React, { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  ELITE_ANSWER_TYPES,
  ELITE_QUESTION_TYPES,
  ELITE_QUIZ_TYPE,
  GAME_LANGUAGES
} from '../../utils/constants';
import Select from 'react-select';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import TextareaAutosize from 'react-textarea-autosize';
const CreateQuestions = ({
  onSubmit,
  quizType,
  mainCategories,
  getSubCategories,
  dropDownData = [],
  subCategories,
  existingQuestion,
  resetTrigger
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    reset,
    getValues
  } = useForm({
    defaultValues: {
      questionType: ELITE_QUESTION_TYPES.MULTIPLE_CHOICE,
      answer: []
    }
  });

  // State for options in Multi Match and Sequence answer types
  const [options, setOptions] = useState([
    { value: 'optionA', label: 'Option A' },
    { value: 'optionB', label: 'Option B' },
    { value: 'optionC', label: 'Option C' },
    { value: 'optionD', label: 'Option D' }
  ]);

  const [dragOptions, setDragOptions] = useState(options);
  const [previewImage, setPreviewImage] = useState(existingQuestion?.image || null);

  // Handle question type selection
  const questionType = useWatch({ control, name: 'questionType' });

  // Handle answer type selection for Multi Match
  const answerType = useWatch({ control, name: 'answerType' });

  // Handle main category selection
  const categoryId = useWatch({
    control,
    name: 'mainCategory'
  });

  // Handle audio type selection
  const audioType = useWatch({
    control,
    name: 'audioType'
  });

  // Handle form submission
  useEffect(() => {
    if (categoryId && getSubCategories) {
      // ✅ check before calling
      getSubCategories(categoryId);

      if (!existingQuestion) {
        setValue('subCategory', '');
      }
    }
  }, [categoryId, existingQuestion]);

  // Populate subCategory if editing an existing question
  useEffect(() => {
    if (existingQuestion) {
      setValue('subCategory', existingQuestion.subCategoryId?._id || '');
      return;
    }
  }, [subCategories, existingQuestion, setValue]);

  // Reset form when resetTrigger changes
  useEffect(() => {
    if (resetTrigger && !existingQuestion) {
      reset({
        questionType: ELITE_QUESTION_TYPES.MULTIPLE_CHOICE,
        language: '',
        mainCategory: '',
        subCategory: '',
        question: '',
        optionA: '',
        optionB: '',
        optionC: '',
        optionD: '',
        answer: '',
        examId: '',
        funLearnId: '',
        level: '',
        mark: '',
        note: '',
        image: null
      });
    }
  }, [resetTrigger, existingQuestion, reset]);

  // Handle image preview for existing questions
  useEffect(() => {
    if (existingQuestion) {
      setPreviewImage(existingQuestion.image || null);
    } else {
      setPreviewImage(null);
    }
  }, [existingQuestion]);

  // Handle question type changes
  useEffect(() => {
    setOptions([
      { value: 'optionA', label: 'Option A' },
      { value: 'optionB', label: 'Option B' },
      ...(questionType != ELITE_QUESTION_TYPES.BOOLEAN
        ? [
            { value: 'optionC', label: 'Option C' },
            { value: 'optionD', label: 'Option D' }
          ]
        : [])
    ]);
  }, [questionType]);

  // Handle form submission
  useEffect(() => {
    setDragOptions(options.map((o) => o));
  }, [options]);

  // Handle question prop for editing
  useEffect(() => {
    if (existingQuestion) {
      // Populate form with question data
      const data = {
        language: existingQuestion.language || '',
        mainCategory: existingQuestion.categoryId?._id || '',
        subCategory: existingQuestion.subCategoryId?._id || '',
        contestId: existingQuestion.contestId || '',
        question: existingQuestion.question || '',
        questionType: existingQuestion.questionType || ELITE_QUESTION_TYPES.MULTIPLE_CHOICE,
        optionA: existingQuestion.options?.[0]?.text || '',
        optionB: existingQuestion.options?.[1]?.text || '',
        optionC: existingQuestion.options?.[2]?.text || '',
        optionD: existingQuestion.options?.[3]?.text || '',
        examId: existingQuestion.examId || '',
        level: existingQuestion.level || '',
        note: existingQuestion.note || '',
        funLearnId: existingQuestion.funLearnId || '',
        mark: existingQuestion.mark || 1,
        audioType: existingQuestion.AudioLink ? 'audioLink' : 'audioUpload'
      };
      if (quizType !== ELITE_QUIZ_TYPE.MULTI_MATCH && quizType !== ELITE_QUIZ_TYPE.GUESS_THE_WORD) {
        data.answer = Object.keys(data).find((key) => {
          return data[key] == existingQuestion.correctAnswer;
        });
        data.answer = {
          value: data.answer,
          label: `Option ${data.answer?.charAt(data.answer?.length - 1)}`
        }; // Convert to object for Select
      } else if (quizType === ELITE_QUIZ_TYPE.MULTI_MATCH) {
        // For Multi Match, handle answer as array
        data.answerType = existingQuestion.answerType || ELITE_ANSWER_TYPES.SINGLE_SELECT;

        data.answer = existingQuestion.correctAnswer.map((ans) =>
          Object.keys(data).find((key) => data[key] === ans)
        );
        data.answer = data.answer.map((ans, idx) => ({
          value: ans,
          label: `Option ${ans.charAt(ans.length - 1)}` // Use last character for label
        }));
        if (existingQuestion.answerType === ELITE_ANSWER_TYPES.SEQUENCE) {
          setDragOptions(data.answer);
        }
      } else {
        // Handle Guess the Word type
        data.answer = existingQuestion.correctAnswer || '';
      }
      reset({
        ...data
      });
    } else {
      // Reset form when no question (create mode)
      reset({
        questionType: ELITE_QUESTION_TYPES.MULTIPLE_CHOICE,
        language: '',
        mainCategory: '',
        subCategory: '',
        contestId: '',
        question: '',
        optionA: '',
        optionB: '',
        optionC: '',
        optionD: '',
        answer: '',
        examId: '',
        funLearnId: '',
        level: '',
        mark: '',
        note: '',
        image: null,
        audioF: ''
      });
    }
  }, [existingQuestion, quizType, reset]);

  // ✅ Set Option A & B values for True/False type
  useEffect(() => {
    if (questionType == ELITE_QUESTION_TYPES.BOOLEAN) {
      setValue('optionA', 'True');
      setValue('optionB', 'False');
    } else if (!existingQuestion) {
      // Only clear when not editing
      setValue('optionA', '');
      setValue('optionB', '');
    }
  }, [questionType, setValue, existingQuestion]);

  // ✅ Set Option C & D values for Multiple Choice type
  useEffect(() => {
    console.log(dragOptions);
    answerType == ELITE_ANSWER_TYPES.SEQUENCE &&
      setValue(
        'answer',
        dragOptions.map((d) => d.value)
      );
  }, [dragOptions, answerType, setValue]);

  //used for drag and drop functionality in multi match and sequence answer types
  function onDragEnd(result) {
    if (!result.destination) return;
    let Items = reorder(dragOptions, result.source.index, result.destination.index);
    setDragOptions(Items);
  }
  /**
   * reorder function to reorder options in drag and drop
   * @param {*} list
   * @param {*} startIndex
   * @param {*} endIndex
   * @returns
   */
  //
  function reorder(list, startIndex, endIndex) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  }

  return (
    <>
      <form
        onSubmit={handleSubmit((data) => {
          onSubmit(data);
          setPreviewImage(null);
        })}
        className="space-y-4 bg-white p-6 rounded shadow m-4"
      >
        <h2 className="text-2xl font-bold text-black-600 mb-4">
          {existingQuestion ? 'Edit Question' : 'Create Questions'}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* 🌍 Language */}
          <div className="w-full sm:flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language <span className="text-red-500">*</span>
            </label>
            <select className="input-company w-full" {...register('language')}>
              <option value="">Select Language</option>
              {GAME_LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.name}
                </option>
              ))}
              {/* Add language options */}
            </select>
          </div>

          {/* 📁 Main Category and Sub Category - Show for Quiz Zone and Guess The Word */}
          {(quizType === ELITE_QUIZ_TYPE.QUIZ_ZONE ||
            quizType === ELITE_QUIZ_TYPE.GUESS_THE_WORD ||
            quizType === ELITE_QUIZ_TYPE.AUDIO_QUESTIONS ||
            quizType === ELITE_QUIZ_TYPE.MULTI_MATCH ||
            quizType === ELITE_QUIZ_TYPE.MATH_QUIZ) && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Main Category <span className="text-red-500">*</span>
                </label>
                <select
                  className="input-company w-full"
                  {...register('mainCategory', { required: 'Main category is required' })}
                >
                  <option value="">Select Main Category</option>
                  {mainCategories?.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.categoryName}
                    </option>
                  ))}
                </select>
                {errors.mainCategory && (
                  <span className="text-red-500 text-sm mt-1 block">
                    {errors.mainCategory.message}
                  </span>
                )}
              </div>

              {/* Sub Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sub Category</label>

                <select className="input-company w-full" {...register('subCategory')}>
                  <option value="">Select Sub Category</option>
                  {subCategories?.map((sc) => (
                    <option key={sc._id} value={sc._id}>
                      {sc.categoryName}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Fun and Learn - Show only for Fun N Learn */}
          {quizType === ELITE_QUIZ_TYPE.FUN_N_LEARN && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fun and Learn <span className="text-red-500">*</span>
              </label>
              <select
                className="input-company w-full"
                {...register('funLearnId', { required: 'Fun and Learn category is required' })}
              >
                <option value="">Select Fun and Learn</option>
                {dropDownData?.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {errors.funLearnId && (
                <span className="text-red-500 text-sm mt-1 block">{errors.funLearnId.message}</span>
              )}
            </div>
          )}
          {quizType === ELITE_QUIZ_TYPE.EXAM_MODULE && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exam Module <span className="text-red-500">*</span>
              </label>
              <select
                className="input-company w-full"
                {...register('examId', { required: 'Exam is required' })}
              >
                <option value="">Select Exam Module</option>
                {dropDownData?.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {errors.examId && (
                <span className="text-red-500 text-sm mt-1 block">{errors.examId.message}</span>
              )}
            </div>
          )}

          {/* Contest - Show only for Contest */}
          {quizType === ELITE_QUIZ_TYPE.CONTEST && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contest <span className="text-red-500">*</span>
              </label>
              <select
                className="input-company w-full"
                {...register('contestId', { required: 'Contest is required' })}
              >
                <option value="">Select Contest</option>
                {dropDownData?.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {errors.contestId && (
                <span className="text-red-500 text-sm mt-1 block">{errors.contestId.message}</span>
              )}
            </div>
          )}
        </div>
        {/* Question Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Question <span className="text-red-500">*</span>
          </label>
          <textarea
            {...register('question', {
              required: `Question is required`,
              onBlur: (e) => {
                const value = e.target.value;
                if (value) {
                  e.target.value = value.charAt(0).toUpperCase() + value.slice(1);
                }
              }
            })}
            rows={3}
            className="mt-1 block w-full input-company border border-gray-300-important  rounded-md shadow-sm  sm:text-sm"
            placeholder={
              quizType === ELITE_QUIZ_TYPE.GUESS_THE_WORD
                ? 'Enter the word or phrase to guess'
                : 'Enter your question'
            }
          />
          {errors.question && (
            <p className="text-red-500 text-xs mt-1">{errors.question.message}</p>
          )}
        </div>

        {/* Question Type + Image Field in Same Row */}
        <div
          className={
            'grid grid-cols-1 ' +
            (quizType === ELITE_QUIZ_TYPE.AUDIO_QUESTIONS ||
            quizType === ELITE_QUIZ_TYPE.MULTI_MATCH
              ? 'md:grid-cols-3'
              : 'md:grid-cols-2') +
            ' gap-4'
          }
        >
          {/* Question Type */}
          {ELITE_QUIZ_TYPE.GUESS_THE_WORD !== quizType && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Question Type <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-4 mt-1">
                <Controller
                  name="questionType"
                  control={control}
                  defaultValue={ELITE_QUESTION_TYPES.MULTIPLE_CHOICE}
                  render={({ field }) => (
                    <div className="flex items-center space-x-4 mt-1">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="questionType"
                          value={ELITE_QUESTION_TYPES.MULTIPLE_CHOICE}
                          checked={field.value == ELITE_QUESTION_TYPES.MULTIPLE_CHOICE}
                          onChange={field.onChange}
                        />
                        <span>Options</span>
                      </label>
                      {/* {quizType != ELITE_QUIZ_TYPE.MULTI_MATCH && (
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="questionType"
                            value={ELITE_QUESTION_TYPES.BOOLEAN}
                            checked={field.value == ELITE_QUESTION_TYPES.BOOLEAN}
                            onChange={field.onChange}
                          />
                          <span>True / False</span>
                        </label>
                      )} */}
                    </div>
                  )}
                />
              </div>
              {errors.questionType && (
                <p className="text-red-500 text-xs mt-1">Question type is required</p>
              )}
            </div>
          )}
          {ELITE_QUIZ_TYPE.MULTI_MATCH == quizType && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Answer Type <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center bg-[#e3eaef] space-x-4 mt-1">
                <Controller
                  name="answerType"
                  control={control}
                  defaultValue={ELITE_ANSWER_TYPES.MULTI_SELECT}
                  render={({ field }) => (
                    <div className="flex items-center space-x-4 mt-1">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="answerType"
                          value={ELITE_ANSWER_TYPES.MULTI_SELECT}
                          checked={field.value == ELITE_ANSWER_TYPES.MULTI_SELECT}
                          onChange={field.onChange}
                        />
                        <span>Multi Select</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="answerType"
                          value={ELITE_ANSWER_TYPES.SEQUENCE}
                          checked={field.value == ELITE_ANSWER_TYPES.SEQUENCE}
                          onChange={field.onChange}
                        />
                        <span>Sequence</span>
                      </label>
                    </div>
                  )}
                />
              </div>
            </div>
          )}

          {/* Image Field */}
          {quizType !== ELITE_QUIZ_TYPE.AUDIO_QUESTIONS && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Image</label>
              <input
                type="file"
                {...register('image')}
                accept="image/png, image/jpeg, image/jpg"
                className="input-company"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setPreviewImage(URL.createObjectURL(file)); // local preview
                  }
                }}
              />
              {previewImage && (
                <div className="relative inline-block mt-2">
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="h-16 w-16 object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPreviewImage(null); // remove preview
                      setValue('image', null); // clear react-hook-form field
                    }}
                    className="absolute -top-2 -right-2 bg-white rounded-full text-black px-1 shadow"
                  >
                    ×
                  </button>
                </div>
              )}
              <p className="text-gray-500 text-xs mt-1">Supported formats: JPG, PNG</p>
            </div>
          )}
          {/* Audio Fields */}
          {quizType === ELITE_QUIZ_TYPE.AUDIO_QUESTIONS && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Audio Type</label>
                <select className="input-company w-full" {...register('audioType')}>
                  <option value="">Select Audio Type</option>
                  <option value="audioLink">Audio Link</option>
                  <option value="audioUpload">Audio Upload</option>
                </select>
                {errors.audioType && (
                  <p className="text-red-500 text-xs mt-1">{errors.audioType.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Audio for Question
                </label>
                {audioType === 'audioLink' ? (
                  <input
                    type="text"
                    {...register('audioLink')}
                    className="input-company w-full"
                    placeholder="Enter audio URL"
                  />
                ) : (
                  <input
                    type="file"
                    {...register('audioFile')}
                    accept="audio/*"
                    className="input-company w-full"
                  />
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Audio type supported (mp3,mp4,wav,ogg,m4a,aac)
                </p>
              </div>
            </>
          )}
        </div>

        {/* Options Fields - Only for Quiz Zone, Fun N Learn and Contest */}
        {quizType != ELITE_QUIZ_TYPE.GUESS_THE_WORD && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {options.map((opt) => {
              return (
                <div key={opt.value}>
                  <label className="block text-sm font-medium text-gray-700">{opt.label} *</label>
                  <input
                    type="textarea"
                    {...register(`${opt.value}`, {
                      required: `${opt.label} is required`,
                      onBlur: (e) => {
                        const value = e.target.value;
                        if (value) {
                          e.target.value = value.charAt(0).toUpperCase() + value.slice(1);
                        }
                      }
                    })}
                    className="input-company"
                    readOnly={questionType === ELITE_QUESTION_TYPES.BOOLEAN}
                  />
                  {errors[`${opt.value}`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`${opt.value}`].message}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Answer Field - Only for Quiz Zone, Fun N Learn and Contest */}

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Answer <span className="text-red-500">*</span>
          </label>
          {quizType != ELITE_QUIZ_TYPE.GUESS_THE_WORD && answerType != ELITE_ANSWER_TYPES.SEQUENCE && (
            <Controller
              name="answer" // The name of your form field
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  {...field}
                  options={options}
                  onChange={(selectedOption) => field.onChange(selectedOption)}
                  isMulti={
                    quizType == ELITE_QUIZ_TYPE.MULTI_MATCH &&
                    answerType == ELITE_ANSWER_TYPES.MULTI_SELECT
                  }
                ></Select>
              )}
            />
          )}
          {quizType == ELITE_QUIZ_TYPE.GUESS_THE_WORD && (
            <input
              type="text"
              {...register('answer', { required: 'Answer is required' })}
              className="input-company"
            />
          )}
          {answerType == ELITE_ANSWER_TYPES.SEQUENCE && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId={'options' + quizType}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="max-h-96 overflow-y-auto bg-gray-100 rounded"
                  >
                    {dragOptions.map((item, idx) => (
                      <Draggable key={'opt' + idx} draggableId={String('opt' + idx)} index={idx}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <div className="draggable"> {item.label}</div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
          {errors.answer && <p className="text-red-500 text-xs mt-1">{errors.answer.message}</p>}
        </div>

        <div className="flex gap-4">
          {(quizType === ELITE_QUIZ_TYPE.QUIZ_ZONE || quizType === ELITE_QUIZ_TYPE.MULTI_MATCH) && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                level <span className="text-red-500">*</span>
              </label>
              <input type="number" {...register('level')} className="input-company" />
              {errors.level && <p className="text-red-500 text-xs mt-1">{errors.level.message}</p>}
            </div>
          )}
          {quizType == ELITE_QUIZ_TYPE.EXAM_MODULE && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Mark <span className="text-red-500">*</span>
              </label>
              <input type="number" {...register('mark')} className="input-company" />
              {errors.mark && <p className="text-red-500 text-xs mt-1">{errors.mark.message}</p>}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <TextareaAutosize
            {...register('note')}
            minRows={3} // starting height
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm resize-none"
          />
          {errors.note && <p className="text-red-500 text-xs mt-1">{errors.note.message}</p>}
        </div>
        {/* Submit Button */}
        <button
          type="submit"
          className="bg-shoorah-active text-white text-sm sm:px-10 py-2 w-full sm:w-auto rounded-full bl</div>ock flex items-center justify-center"
        >
          {existingQuestion ? 'Update Question' : 'Create Question'}
        </button>
      </form>
    </>
  );
};

export default CreateQuestions;
